import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import { z } from 'zod';

export const slackMessageTypeEnum = z.enum([
  'feedback',
  'signup',
  'workSignup',
  'nps',
  'newTeam',
  'completePurchase',
  'rewardful',
  'dispute',
]);

type SlackMessageType = z.infer<typeof slackMessageTypeEnum>;

export const formatTimeAgo = (date: Date) => {
  const now = new Date();

  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let timeAgo: string;
  if (now.toDateString() === date.toDateString()) {
    timeAgo = 'today';
  } else if (diffDays === 1) {
    timeAgo = 'yesterday';
  } else if (diffDays < 7) {
    timeAgo = `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    timeAgo = `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(diffDays / 30);
    timeAgo = `${months} month${months > 1 ? 's' : ''} ago`;
  }
  return timeAgo;
};

export async function sendMessageToSlack({
  message,
  type,
}: {
  message: string;
  type: SlackMessageType;
}) {
  const baseWebhookURL = process.env.SLACK_WEBHOOK_BASE_URL;
  let webhookSuffix: string | undefined = '';

  switch (type) {
    case 'feedback':
      webhookSuffix = process.env.SLACK_WEBHOOK_FEEDBACK;
      break;
    case 'signup':
      webhookSuffix = process.env.SLACK_WEBHOOK_COMPANY_SIGNUP;
      break;
    case 'workSignup':
      webhookSuffix = process.env.SLACK_WEBHOOK_WORK;
      break;
    case 'nps':
      webhookSuffix = process.env.SLACK_WEBHOOK_NPS;
      break;
    case 'newTeam':
      webhookSuffix = process.env.SLACK_WEBHOOK_TEAM;
      break;
    case 'completePurchase':
    case 'dispute':
      webhookSuffix = process.env.SLACK_WEBHOOK_COMPLETE_PURCHASE;
      break;
    case 'rewardful':
      webhookSuffix = process.env.SLACK_WEBHOOK_REWARDFUL;
      break;
    default:
      console.error('Invalid message type specified');
      return false;
  }

  const webhookURL = `${baseWebhookURL}${webhookSuffix}`;

  const payload = { text: message };

  try {
    const response = await axios.post(webhookURL, payload);
    return response.status === 200;
  } catch (error) {
    Sentry.captureException(error);
    console.error('Error sending message to Slack:', error);
    return false;
  }
}
